import React from 'react';
import { BaseContainerEffect, ContainerEffectConfig, LayerType } from './BaseContainerEffect';
import { EasingFunction, Easing } from './BaseEffect';

/**
 * Configuration interface specific to zoom effects
 */
export interface ContainerZoomEffectConfig {
    targetLayers: LayerType[];
    excludeLayers?: LayerType[];
    scaleFrom: number;           // Starting scale (1 = 100%)
    scaleTo: number;             // Ending scale (1 = 100%)
    duration: number;
    at: number;
    transformOrigin?: string;    // CSS transform-origin (default: 'center center')
    easing?: EasingFunction;
}

/**
 * ContainerZoomEffect - Container-based zoom/scale effect
 * 
 * Groups specified layers into a single container and applies a unified
 * zoom/scale transformation to the entire container. This maintains natural
 * spatial relationships between layers during scaling.
 * 
 * Benefits of container-based zoom:
 * - Layers maintain relative positioning during zoom
 * - Natural movement where content scales as a unit
 * - Better performance with single transform operation
 * - Consistent zoom center point for all layers
 * 
 * Use cases:
 * - Ken Burns effect on background and scene
 * - Dramatic zoom transitions between scenes
 * - Focus effects that scale content groups
 * - Cinematic zoom effects on multiple layers
 */
export class ContainerZoomEffect extends BaseContainerEffect {
    private transformOrigin: string;

    constructor(config: ContainerZoomEffectConfig) {
        super({
            targetLayers: config.targetLayers,
            excludeLayers: config.excludeLayers,
            from: config.scaleFrom,
            to: config.scaleTo,
            duration: config.duration,
            at: config.at,
            easing: config.easing
        });
        this.transformOrigin = config.transformOrigin || 'center center';
    }

    getContainerStyle(progress: number): React.CSSProperties {
        const currentScale = this.from + (this.to - this.from) * progress;
        
        return {
            transform: `scale(${currentScale})`,
            transformOrigin: this.transformOrigin
        };
    }

    getEffectType(): string {
        return 'container-zoom';
    }

    // Static factory methods for common zoom patterns

    /**
     * Create Ken Burns effect - slow zoom in on background content
     * Classic documentary-style slow zoom
     */
    static createKenBurnsZoom(
        duration: number,
        at: number,
        zoomAmount: number = 1.2,
        easing?: EasingFunction
    ): ContainerZoomEffect {
        return new ContainerZoomEffect({
            targetLayers: ['background', 'scene'],
            scaleFrom: 1,
            scaleTo: zoomAmount,
            duration,
            at,
            transformOrigin: 'center center',
            easing: easing || Easing.easeInOutSine
        });
    }

    /**
     * Create dramatic zoom in effect - fast zoom for impact
     * Good for dramatic reveals or emphasis
     */
    static createDramaticZoomIn(
        duration: number,
        at: number,
        zoomAmount: number = 1.5,
        easing?: EasingFunction
    ): ContainerZoomEffect {
        return new ContainerZoomEffect({
            targetLayers: ['background', 'scene', 'overlay'],
            scaleFrom: 1,
            scaleTo: zoomAmount,
            duration,
            at,
            transformOrigin: 'center center',
            easing: easing || Easing.easeOutCubic
        });
    }

    /**
     * Create zoom out effect - pull back to reveal more content
     * Good for establishing shots or revealing context
     */
    static createZoomOut(
        duration: number,
        at: number,
        zoomAmount: number = 0.8,
        easing?: EasingFunction
    ): ContainerZoomEffect {
        return new ContainerZoomEffect({
            targetLayers: ['background', 'scene'],
            excludeLayers: ['textOverlay', 'caption', 'branding'],
            scaleFrom: 1,
            scaleTo: zoomAmount,
            duration,
            at,
            transformOrigin: 'center center',
            easing: easing || Easing.easeInOutCubic
        });
    }

    /**
     * Create punch zoom effect - zoom in then back out
     * Creates two effects for in/out motion
     */
    static createPunchZoom(
        duration: number,
        at: number,
        maxZoom: number = 1.3
    ): [ContainerZoomEffect, ContainerZoomEffect] {
        const zoomIn = new ContainerZoomEffect({
            targetLayers: ['background', 'scene', 'overlay'],
            excludeLayers: ['textOverlay', 'caption', 'branding'],
            scaleFrom: 1,
            scaleTo: maxZoom,
            duration: duration / 2,
            at,
            transformOrigin: 'center center',
            easing: Easing.easeOutCubic
        });

        const zoomOut = new ContainerZoomEffect({
            targetLayers: ['background', 'scene', 'overlay'],
            excludeLayers: ['textOverlay', 'caption', 'branding'],
            scaleFrom: maxZoom,
            scaleTo: 1,
            duration: duration / 2,
            at: at + duration / 2,
            transformOrigin: 'center center',
            easing: Easing.easeInCubic
        });

        return [zoomIn, zoomOut];
    }

    /**
     * Create corner-focused zoom - zoom from specific corner
     * Good for focusing on corner elements
     */
    static createCornerZoom(
        duration: number,
        at: number,
        corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right',
        zoomAmount: number = 1.2,
        easing?: EasingFunction
    ): ContainerZoomEffect {
        const originMap = {
            'top-left': 'top left',
            'top-right': 'top right', 
            'bottom-left': 'bottom left',
            'bottom-right': 'bottom right'
        };

        return new ContainerZoomEffect({
            targetLayers: ['background', 'scene'],
            scaleFrom: 1,
            scaleTo: zoomAmount,
            duration,
            at,
            transformOrigin: originMap[corner],
            easing: easing || Easing.easeOutCubic
        });
    }

    /**
     * Create custom zoom effect with full control
     */
    static createCustom(config: ContainerZoomEffectConfig): ContainerZoomEffect {
        return new ContainerZoomEffect(config);
    }

    /**
     * Create zoom effect from percentage values (100 = 100%)
     * Convenience method for users thinking in percentages
     */
    static createFromPercentage(
        targetLayers: LayerType[],
        fromPercent: number,
        toPercent: number,
        duration: number,
        at: number,
        transformOrigin?: string,
        easing?: EasingFunction
    ): ContainerZoomEffect {
        return new ContainerZoomEffect({
            targetLayers,
            scaleFrom: fromPercent / 100,
            scaleTo: toPercent / 100,
            duration,
            at,
            transformOrigin,
            easing
        });
    }
}